import { prepareHealthFiles, type HealthEntry } from './health';
import { cleanForeground } from './healthPhotoMask';
export const PHOTO_WIDTH = 1080, PHOTO_HEIGHT = 1440;
export type EncodedPhoto = { mimeType: string; base64: string };
export type PhotoUpload = { files: EncodedPhoto[]; pose: string; photoFormat: 'aligned-v1' };
export type Bounds = { x: number; y: number; width: number; height: number };
export type PhotoAdjust = { zoom: number; x: number; y: number; background: string };
export const defaultAdjust: PhotoAdjust = { zoom: 1, x: 0, y: 0, background: '#f8f7f3' };
export type PhotoSource = { original: HTMLImageElement; encoded: EncodedPhoto; cutout: HTMLCanvasElement; bounds: Bounds; warning?: string };
export const photoPath = (entry: HealthEntry) => entry.media[0];
export function photoTransform(bounds: Bounds, adjust: PhotoAdjust) {
  // One scalar for both axes: changing body width never changes normalization.
  const scale = PHOTO_HEIGHT * 0.84 / bounds.height * adjust.zoom;
  return { scale, x: PHOTO_WIDTH / 2 - (bounds.x + bounds.width / 2) * scale + adjust.x * PHOTO_WIDTH,
    y: PHOTO_HEIGHT * 0.92 - (bounds.y + bounds.height) * scale + adjust.y * PHOTO_HEIGHT };
}
export function canvas(width: number, height: number) { const c = document.createElement('canvas'); c.width = width; c.height = height; return c; }
export function context(c: HTMLCanvasElement) { const ctx = c.getContext('2d'); if (!ctx) throw new Error('Photo editing is unavailable in this browser.'); return ctx; }
export async function loadPhoto(url: string) { const image = new Image(); image.src = url; await image.decode(); return image; }
export async function preparePhoto(file: File): Promise<PhotoSource> {
  const [encoded] = await prepareHealthFiles([file]);
  const original = await loadPhoto(`data:${encoded.mimeType};base64,${encoded.base64}`);
  try {
  const { ImageSegmenter, FilesetResolver } = await import('@mediapipe/tasks-vision');
  const resolver = await FilesetResolver.forVisionTasks('/photo-engine-v1/wasm');
  const segmenter = await ImageSegmenter.createFromOptions(resolver, { baseOptions: { modelAssetPath: '/photo-engine-v1/selfie_segmenter.tflite', delegate: 'CPU' }, runningMode: 'IMAGE', outputConfidenceMasks: true, outputCategoryMask: false });
  try {
    const result = segmenter.segment(original);
    try {
      // The pinned binary selfie model has one confidence output: foreground.
      const mask = result.confidenceMasks?.[0];
      if (!mask) throw new Error('A person could not be isolated. Try a clearer full-body photo.');
      const w = mask.width, h = mask.height, values = cleanForeground(mask.getAsFloat32Array(), w, h);
      const alpha = canvas(w, h), ctx = context(alpha), pixels = ctx.createImageData(w, h);
      let left = w, right = -1, top = h, bottom = -1, count = 0;
      for (let i = 0; i < values.length; i++) {
        const v = Math.max(0, Math.min(1, (values[i] - 0.15) / 0.7));
        pixels.data[i * 4 + 3] = Math.round(v * 255);
        if (values[i] > 0.65) { const x = i % w, y = Math.floor(i / w); left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y); count++; }
      }
      if (count < w * h * 0.02 || bottom - top < h * 0.2) throw new Error('A person could not be isolated. Try a clearer full-body photo.');
      ctx.putImageData(pixels, 0, 0);
      const cutout = canvas(original.naturalWidth, original.naturalHeight), cut = context(cutout);
      cut.drawImage(original, 0, 0); cut.globalCompositeOperation = 'destination-in'; cut.drawImage(alpha, 0, 0, cutout.width, cutout.height); cut.globalCompositeOperation = 'source-over';
      const sx = cutout.width / w, sy = cutout.height / h;
      return { original, encoded, cutout, bounds: { x: left * sx, y: top * sy, width: (right - left + 1) * sx, height: (bottom - top + 1) * sy } };
    } finally { result.close(); }
  } finally { segmenter.close(); }
  } catch {
    const cutout = canvas(original.naturalWidth, original.naturalHeight); context(cutout).drawImage(original, 0, 0);
    return { original, encoded, cutout, bounds: { x: 0, y: 0, width: cutout.width, height: cutout.height }, warning: 'Background removal could not finish. You can frame the source photo below, or choose another clearer photo to try again.' };
  }
}
export function drawPhoto(target: HTMLCanvasElement, source: PhotoSource, adjust: PhotoAdjust, removeBackground = true) {
  target.width = PHOTO_WIDTH; target.height = PHOTO_HEIGHT;
  const ctx = context(target), t = photoTransform(source.bounds, adjust);
  ctx.fillStyle = adjust.background; ctx.fillRect(0, 0, target.width, target.height);
  ctx.drawImage(removeBackground ? source.cutout : source.original, t.x, t.y, source.cutout.width * t.scale, source.cutout.height * t.scale);
}
export function encodePhoto(c: HTMLCanvasElement): EncodedPhoto {
  for (const quality of [0.92, 0.84, 0.72, 0.6]) { const base64 = c.toDataURL('image/jpeg', quality).split(',')[1]; if (base64.length <= 2700000) return { mimeType: 'image/jpeg', base64 }; }
  throw new Error('The prepared photo is too large. Try a simpler background.');
}
